/**
 * 회원 관리 UI 배지 컴포넌트
 */

import React from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import type { MembershipType, MemberStatus } from '../models';
import { expiryDateKST } from '../../../utils/dateUtils';

/**
 * 만료일(마지막 이용 가능일)을 MM.DD 로 — KST 기준
 */
const formatShortDate = (dateString?: string): string => {
  const ymd = expiryDateKST(dateString);
  if (!ymd) return dateString || '';
  const [, month, day] = ymd.split('-');
  return `${month}.${day}`;
};

/**
 * 이용권 타입 배지 렌더링
 */
export const getMembershipBadge = (
  type: MembershipType,
  remainingTime?: number,
  expiryDate?: string,
  voucherName?: string
): JSX.Element => {
  // 상품명이 있으면 상품명 사용, 없으면 기본 타입명
  const getDisplayName = () => {
    if (voucherName) return voucherName;
    switch (type) {
      case 'hourly': return '시간권';
      case 'daily': return '일일권';
      case 'monthly': return '정기권';
      default: return '';
    }
  };

  switch (type) {
    case 'hourly':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {getDisplayName()} ({remainingTime}시간)
        </span>
      );
    case 'daily':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          {getDisplayName()}
        </span>
      );
    case 'monthly':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          {getDisplayName()} (~{formatShortDate(expiryDate)})
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          이용권 없음
        </span>
      );
  }
};

/**
 * 회원 상태 배지 렌더링
 */
export const getStatusBadge = (status: MemberStatus): JSX.Element => {
  switch (status) {
    case 'active':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="h-3 w-3 mr-1" />
          정상
        </span>
      );
    case 'waiting':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
          대기
        </span>
      );
    case 'inactive':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <XCircleIcon className="h-3 w-3 mr-1" />
          퇴원
        </span>
      );
    case 'suspended':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
          정지
        </span>
      );
    case 'expired':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <ClockIcon className="h-3 w-3 mr-1" />
          만료
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          알 수 없음
        </span>
      );
  }
};
